import { useEffect, useRef, useState } from 'react';
import { hexToRgba, rgbaToCss, rgbaToHex } from '../core/color';
import type { ToolId } from '../core/types';
import { useAppStore } from '../state/store';
import { TOOLBAR_SECTIONS, type ToolDescriptor } from '../tools';
import { Icon } from './components/controls';

const HOLD_MS = 280;

function slotKey(section: number, slot: number): string {
  return `${section}:${slot}`;
}

function visibleTool(
  tools: ToolDescriptor[],
  active: ToolId,
  remembered: ToolId | undefined,
): ToolDescriptor {
  if (tools.some((tool) => tool.id === active)) {
    return tools.find((tool) => tool.id === active)!;
  }
  if (remembered) {
    const match = tools.find((tool) => tool.id === remembered);
    if (match) return match;
  }
  return tools[0]!;
}

function ToolSlot({
  tools,
  open,
  onOpen,
  onClose,
}: {
  tools: ToolDescriptor[];
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const tool = useAppStore((state) => state.tool);
  const setTool = useAppStore((state) => state.setTool);
  const [remembered, setRemembered] = useState<ToolId>(tools[0]!.id);
  const holdTimer = useRef<number | null>(null);
  const openedByHold = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const shown = visibleTool(tools, tool, remembered);
  const nested = tools.length > 1;
  const active = tools.some((entry) => entry.id === tool);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  const clearHold = () => {
    if (holdTimer.current !== null) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  };

  const select = (id: ToolId) => {
    setRemembered(id);
    setTool(id);
    onClose();
  };

  return (
    <div className="toolbar__slot" ref={rootRef}>
      <button
        type="button"
        className={`toolbar__button${active ? ' toolbar__button--active' : ''}${
          nested ? ' toolbar__button--nested' : ''
        }`}
        title={
          nested
            ? `${shown.label} (${shown.shortcut}) — hold or right-click for more`
            : `${shown.label} (${shown.shortcut})`
        }
        onPointerDown={(event) => {
          if (event.button !== 0 || !nested) return;
          openedByHold.current = false;
          clearHold();
          holdTimer.current = window.setTimeout(() => {
            openedByHold.current = true;
            onOpen();
          }, HOLD_MS);
        }}
        onPointerUp={() => {
          clearHold();
        }}
        onPointerLeave={() => {
          clearHold();
        }}
        onClick={() => {
          if (openedByHold.current) {
            openedByHold.current = false;
            return;
          }
          select(shown.id);
        }}
        onContextMenu={(event) => {
          if (!nested) return;
          event.preventDefault();
          onOpen();
        }}
      >
        <Icon path={shown.icon} />
        {nested && <span className="toolbar__nest" aria-hidden />}
      </button>

      {open && nested && (
        <div className="toolbar__flyout" role="menu">
          {tools.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="menuitem"
              className={`toolbar__flyout-item${
                tool === entry.id ? ' toolbar__flyout-item--active' : ''
              }`}
              title={`${entry.label} (${entry.shortcut})`}
              onClick={() => select(entry.id)}
            >
              <Icon path={entry.icon} />
              <span className="toolbar__flyout-label">{entry.label}</span>
              <span className="toolbar__flyout-shortcut">{entry.shortcut}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Toolbar() {
  const foreground = useAppStore((state) => state.foreground);
  const background = useAppStore((state) => state.background);
  const setForeground = useAppStore((state) => state.setForeground);
  const setBackground = useAppStore((state) => state.setBackground);
  const swapColors = useAppStore((state) => state.swapColors);
  const [openSlot, setOpenSlot] = useState<string | null>(null);

  return (
    <div className="toolbar">
      <div className="toolbar__tools">
        {TOOLBAR_SECTIONS.map((section, sectionIndex) => (
          <div key={sectionIndex} style={{ display: 'contents' }}>
            {sectionIndex > 0 && <div className="toolbar__separator" />}
            {section.map((tools, slotIndex) => {
              const id = slotKey(sectionIndex, slotIndex);
              return (
                <ToolSlot
                  key={id}
                  tools={tools}
                  open={openSlot === id}
                  onOpen={() => setOpenSlot(id)}
                  onClose={() => setOpenSlot((current) => (current === id ? null : current))}
                />
              );
            })}
          </div>
        ))}
      </div>

      <div className="toolbar__separator toolbar__separator--footer" />
      <div className="toolbar__footer">
        <div className="toolbar__swatches" title="Foreground and background color (X swaps, D resets)">
          <label
            className="toolbar__swatch toolbar__swatch--fg"
            style={{ background: rgbaToCss(foreground) }}
          >
            <input
              type="color"
              style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
              value={rgbaToHex(foreground)}
              onChange={(event) => setForeground(hexToRgba(event.target.value))}
            />
          </label>
          <label
            className="toolbar__swatch toolbar__swatch--bg"
            style={{ background: rgbaToCss(background) }}
          >
            <input
              type="color"
              style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
              value={rgbaToHex(background)}
              onChange={(event) => setBackground(hexToRgba(event.target.value))}
            />
          </label>
        </div>
        <button
          type="button"
          className="toolbar__button"
          title="Swap colors (X)"
          onClick={swapColors}
        >
          <Icon path="M7 7h10l-3-3M17 17H7l3 3" size={14} />
        </button>
      </div>
    </div>
  );
}
